/**
 * The library's list of songs.
 *
 * Was a 2–3 column grid of tiles; it is a single column of rows now, because a
 * music library is a track list. The name is kept: `SongGrid` is what the route
 * imports, and renaming it would touch files this redesign has no business in.
 *
 * The `lyx-song-grid` class still carries the layout from the global stylesheet —
 * the responsive rules are media queries, so they cannot be inline styles.
 */
import SongCard from './SongCard';
import type { Song } from '../../types/domain';

export interface SongGridProps {
  songs: Song[];
  /** Id of the song in the player, running or paused, or null. */
  playingId: string | null;
  /** True when the loaded song is paused — see SongCard's `active`. */
  paused?: boolean;
  elapsed: number;
  /** Playback rate, for the active row's scrubber smoothing. Defaults to 1×. */
  speed?: number;
  /** True while the active song's real audio is loading/buffering. */
  loading?: boolean;
  /**
   * What clicking a row does. See SongCard: this is no longer "open the Studio".
   *
   * Renamed from `onOpen` along with the prop on the row, because the old name meant a
   * destination and every caller satisfied it with a Studio navigation.
   */
  onSelect: (song: Song) => void;
  /** Opens a song in the Studio, from the row's More menu. Omitted means no such item. */
  onOpenInStudio?: (song: Song) => void;
  onTogglePlay: (songId: string) => void;
  onSeek?: (songId: string, seconds: number) => void;
  /**
   * Plays a song in the mini player, from the row's More menu — starts it and raises the
   * floating bar. Omitted means no such item, like the Studio one above.
   */
  onPlayInMiniPlayer?: (song: Song) => void;
  onToggleFavorite: (songId: string) => void;
  onEdit: (song: Song) => void;
  onDuplicate: (song: Song) => void;
  onDownload: (song: Song) => void;
  onDelete: (song: Song) => void;
  onAddToPlaylist: (song: Song) => void;
  /**
   * Raises the listing confirmations. Optional, as on the row: a surface that lists songs
   * without owning the discovery decision passes neither and its rows are unchanged.
   */
  onMakeDiscoverable?: (song: Song) => void;
  onRemoveFromCatalogue?: (song: Song) => void;
}

export default function SongGrid({
  songs,
  playingId,
  paused = false,
  elapsed,
  speed = 1,
  loading = false,
  ...handlers
}: SongGridProps) {
  return (
    <div className="lyx-song-grid">
      {songs.map((song) => (
        <SongCard
          key={song.id}
          song={song}
          // Active is the loaded song either way; playing is the loaded song in
          // motion. The row keeps its highlight and its scrubber through a pause and
          // only the button's label changes, which is what a pause is.
          active={playingId === song.id}
          playing={playingId === song.id && !paused}
          elapsed={elapsed}
          speed={speed}
          loading={playingId === song.id && loading}
          {...handlers}
        />
      ))}
    </div>
  );
}
